/* Product star-rating block — compact aggregate under the product title.
 *
 * Tiny companion to reviews-widget.js. Uses the shared
 * window.YukiReviews.fetchList cache so on a product page where the
 * full reviews widget section is ALSO present we don't fire two
 * /apps/api/reviews/list calls.
 */
(function () {
  if (window.__productStarRatingInit) return;
  window.__productStarRatingInit = true;

  const API_BASE = '/apps/api/reviews';

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-product-star-rating]').forEach(initBlock);
  });

  function initBlock(root) {
    const target = root.dataset.target;
    const type = root.dataset.type;
    if (!target || !type) { root.style.display = 'none'; return; }
    const showAvg = root.dataset.showAvg !== 'false';
    const showCount = root.dataset.showCount !== 'false';
    const countFormat = root.dataset.countFormat || 'long';
    const scrollToReviews = root.dataset.scrollToReviews !== 'false';
    const hideWhenEmpty = root.dataset.hideWhenEmpty === 'true';

    const opts = { showAvg, showCount, countFormat, scrollToReviews };

    // Make the WHOLE block scroll to the reviews widget on click. Attaching
    // here (to the persistent root) rather than to the async-rendered inner
    // link is robust to both render paths (product + shop) and to any markup.
    if (scrollToReviews) {
      root.style.cursor = 'pointer';
      root.addEventListener('click', handleScrollClick);
    }

    fetchAggregate(target, type)
      .then(function (agg) {
        if (agg.count > 0) {
          renderInto(root, agg, opts);
          return;
        }
        // No reviews on THIS product yet → fall back to the overall shop
        // rating (social proof). If the shop has no approved reviews either,
        // honour the original hide/empty behaviour.
        return fetchShopAggregate().then(function (shopAgg) {
          if (shopAgg && shopAgg.count > 0) {
            root.setAttribute('title', 'Overall store rating');
            renderInto(root, shopAgg, opts);
          } else if (hideWhenEmpty) {
            root.style.display = 'none';
          } else {
            renderInto(root, agg, opts);
          }
        });
      })
      .catch(function (err) {
        console.warn('[product-star-rating] fetch failed:', err);
        if (hideWhenEmpty) root.style.display = 'none';
      });
  }

  function fetchShopAggregate() {
    // Shop-wide aggregate across ALL approved reviews — same endpoint that
    // powers the homepage carousel + /pages/shop-reviews. per_page=1 keeps the
    // payload tiny (we only read `.aggregate`). min_rating=1 is irrelevant to
    // the aggregate (always computed over the full approved set) but explicit.
    const url = API_BASE + '/shop-list?per_page=1&min_rating=1';
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('shop_' + r.status); return r.json(); })
      .then(function (data) { return data.aggregate; })
      .catch(function () { return null; });
  }

  function fetchAggregate(target, type) {
    // Prefer the shared cache when reviews-widget.js has loaded.
    if (window.YukiReviews && typeof window.YukiReviews.fetchList === 'function') {
      return window.YukiReviews.fetchList(target, type, 1, 10).then(function (data) {
        return { count: data.aggregate.count, avg_rating: data.aggregate.avg_rating };
      });
    }
    // Fallback: independent fetch (per_page=1 to minimize payload — we
    // only need the aggregate).
    const url = API_BASE + '/list?target=' + encodeURIComponent(target)
      + '&type=' + encodeURIComponent(type) + '&page=1&per_page=1';
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('list_' + r.status);
        return r.json();
      })
      .then(function (data) { return data.aggregate; });
  }

  function renderInto(root, agg, opts) {
    const loadingEl = root.querySelector('[data-psr-loading]');
    const starsHtml = renderStars(agg.avg_rating);
    const avgHtml = opts.showAvg && agg.count > 0
      ? '<span class="psr-avg">' + (Math.round(agg.avg_rating * 10) / 10).toFixed(1) + '</span>'
      : '';
    const countHtml = opts.showCount ? '<span class="psr-count">' + formatCount(agg.count, opts.countFormat) + '</span>' : '';
    const inner = ''
      + '<span class="psr-stars">' + starsHtml + '</span>'
      + avgHtml + countHtml;
    const html = opts.scrollToReviews
      ? '<a class="psr-link" href="#reviews" data-psr-link>' + inner + '</a>'
      : inner;
    if (loadingEl) loadingEl.remove();
    root.insertAdjacentHTML('beforeend', html);
    // Click handling is bound once on the block root in initBlock (covers the
    // stars + count + this link), so nothing to wire per-render here.
  }

  function handleScrollClick(e) {
    // Find the reviews area. Prefer an explicit #reviews anchor, then the
    // reviews-widget section root / its tab wrapper, then legacy classes.
    var target = document.querySelector('#reviews')
      || document.querySelector('[data-reviews-widget]')
      || document.querySelector('.rw-tabs')
      || document.querySelector('.rw-section')
      || document.querySelector('.reviews-widget');
    if (!target) return; // no reviews widget on the page → let any href run
    e.preventDefault();
    // Offset for Dawn's sticky header so the heading isn't tucked underneath.
    var headerH = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
    ) || 0;
    var top = target.getBoundingClientRect().top + window.pageYOffset - headerH - 12;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function renderStars(rating) {
    const rounded = Math.round(rating);
    let out = '';
    for (let i = 1; i <= 5; i++) {
      out += '<span class="psr-star' + (i <= rounded ? ' is-filled' : '') + '">★</span>';
    }
    return out;
  }

  function formatCount(n, format) {
    if (format === 'short') return '(' + n + ')';
    return n + ' ' + (n === 1 ? 'review' : 'reviews');
  }
})();
